import { getContentfulGqlClient } from "@/contentful/wac-content-gql-client.ts";

import { mapTemplateItem } from "../map-template-item";

import { allProductTemplatesQuery } from "./gql.ts";

import type {
    AllProductTemplatesData,
    AllProductTemplatesQueryVariables,
    ContentfulGalleryDataSource,
    ContentfulGalleryDataType,
    ContentfulGalleryLocale,
    ContentfulGalleryPage,
    ContentfulGalleryPageArgs,
    GqlTemplate
} from "./types.ts";

const GQL_FETCH_LIMIT = 100;

export interface GetContentfulGalleryItemsArgs {
    contentType: ContentfulGalleryDataType;
    loaded: number;
    locale: ContentfulGalleryLocale;
}

const contentfulGalleryDataSources: Record<
    ContentfulGalleryDataType,
    ContentfulGalleryDataSource
> = {
    productTemplate: {
        fetchPage: fetchProductTemplatePage,
        mapItem: (item) => mapTemplateItem(item as GqlTemplate)
    }
};

export const getContentfulGalleryItems = async ({
    contentType,
    loaded,
    locale
}: GetContentfulGalleryItemsArgs): Promise<unknown[]> => {
    const dataSource = contentfulGalleryDataSources[contentType];
    const cacheKey = getCacheKey({ contentType, loaded, locale });
    const cached = getWindowCache(cacheKey);
    if (cached) {
        return cached;
    }

    const firstPage = await dataSource.fetchPage({
        limit: GQL_FETCH_LIMIT,
        skip: loaded,
        locale
    });
    const allItems = firstPage.items.map(dataSource.mapItem);

    const remainingSkips = getRemainingSkips({
        loaded,
        total: firstPage.total,
        limit: GQL_FETCH_LIMIT
    });
    const remainingPages = await Promise.all(
        remainingSkips.map((skip) =>
            dataSource.fetchPage({
                limit: GQL_FETCH_LIMIT,
                skip,
                locale
            })
        )
    );

    for (const page of remainingPages) {
        allItems.push(...page.items.map(dataSource.mapItem));
    }

    setWindowCache(cacheKey, allItems);
    return allItems;
};

async function fetchProductTemplatePage({
    limit,
    skip,
    locale
}: ContentfulGalleryPageArgs): Promise<ContentfulGalleryPage> {
    const result = await getContentfulGqlClient()
        .query<AllProductTemplatesData, AllProductTemplatesQueryVariables>(
            allProductTemplatesQuery,
            { limit, skip, locale }
        )
        .toPromise();

    const collection = result.data?.productTemplateCollection;
    return {
        total: collection?.total ?? 0,
        items: collection?.items ?? []
    };
}

function getRemainingSkips({
    loaded,
    total,
    limit
}: {
    loaded: number;
    total: number;
    limit: number;
}): number[] {
    const skips: number[] = [];
    for (let skip = loaded + limit; skip < total; skip += limit) {
        skips.push(skip);
    }
    return skips;
}

function getCacheKey({
    contentType,
    loaded,
    locale
}: {
    contentType: ContentfulGalleryDataType;
    loaded: number;
    locale: ContentfulGalleryLocale;
}): string {
    return `__UTG_${contentType.toUpperCase()}_LOCALE_${locale}_FROM_${loaded}__`;
}

function getWindowCache(key: string): unknown[] | null {
    const cached = (window as unknown as Record<string, unknown>)[key];
    return Array.isArray(cached) ? (cached as unknown[]) : null;
}

function setWindowCache(key: string, items: unknown[]): void {
    (window as unknown as Record<string, unknown>)[key] = items;
}
